import React, { ReactNode, useContext, useEffect, useState } from 'react';

import { useFetch } from '@uservoice/rest-utils';
import { generatePath, Prompt, useHistory, useParams } from 'react-router-dom';

import { Team } from '@/core/api/collections/teams';
import {
  getUser,
  ProductType,
  updateUserPermissions,
  updateUserTeams,
  User,
  UserParams,
  grantAccess,
  revokeAccess,
  resendInvitation as doResendInvitation,
} from '@/core/api/collections/users';
import { AuthContext } from '@/core/auth/AuthContext';
import { DEFAULT_CORE_PERMISSIONS } from '@/core/users/UserPermissions/constants';
import useOnExitPrompt, { UNSAVED_WARNING_MESSAGE } from '@/core/users/UserPermissions/useOnExitPrompt';
import { UserEditValidationErrors } from '@/core/users/UserPermissions/userEditValidators';
import { UserPermissionTab } from '@/core/users/UserPermissions/UserPermissionsTabs';
import { calculatePerAdminBillingRateCents } from '@/core/utils/account';
import routes from '@/routes';
import { useSnackbar } from '@/SnackbarProvider';

type EditedUser = User & {
  updateAccessDiscovery?: boolean;
  updateAccessValidation?: boolean;
};

export interface UserEditCtx {
  user?: EditedUser;
  updateUser: (user: User) => void;
  updateTeam: (team: Team, isSelected: boolean) => void;
  handlePersist: () => void;
  isDirty: boolean;
  isPersisting: boolean;
  loading: boolean;
  errors: UserEditValidationErrors;
  confirmModalOpen: boolean;
  setConfirmModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setErrors: (errors: UserEditValidationErrors) => void;
  toggleAccess: (product: ProductType) => void;
  resendInvitation: (id: number) => void;
  resendingInvitation: boolean;
  billingRate?: number;
}

export const UserEditContext = React.createContext<UserEditCtx>({} as UserEditCtx);

type Props = {
  user?: User;
  children: ReactNode;
}
export const UserEditContextProvider = ({ children }: Props) => {
  const history = useHistory();
  const { currentAccount, updateBillingRate } = useContext(AuthContext);
  const { enqueueSnackbarError, enqueueSnackbar } = useSnackbar();
  const { userId } = useParams() as { userId: string, tab?: UserPermissionTab };
  const [fetchedUser, loading, error] = useFetch<UserParams, User>(getUser, { userId });

  const [editedUser, setEditedUser] = useState<EditedUser | undefined>(fetchedUser);
  const [teamToggles, setTeamToggles] = useState({} as { [key: number]: 'on' | 'off' | undefined });

  const [errors, setErrors] = useState({} as UserEditValidationErrors);
  const [isDirty, setIsDirty] = useState(false);
  const [isPersisting, setIsPersisting] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [resendingInvitation, setResendingInvitation] = useState(false);
  const [billingRate, setBillingRate] = useState<number | undefined>(undefined);

  useOnExitPrompt(isDirty);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (fetchedUser) {
      setEditedUser(fetchedUser);
    }
  }, [fetchedUser]);

  useEffect(() => {
    if (error?.response?.status === 404) {
      setTimeout(() => history.replace(routes.users.self));
    }
  }, [error]);

  if (error?.response?.status === 404) {
    return null;
  }

  const updateTeam = (team: Team, isSelected: boolean) => {
    if (editedUser) {
      if (isSelected) {
        teamToggles[team.id] = teamToggles[team.id] === 'on' ? undefined : 'on';
        editedUser.teams.push(team);
      } else {
        teamToggles[team.id] = teamToggles[team.id] === 'off' ? undefined : 'off';
        editedUser.teams = editedUser.teams.reduce((prev, current) => (current.id === team.id ? prev : [...prev, current]), [] as Team[]);
      }
      setTeamToggles({ ...teamToggles });
      updateUser(editedUser);
    }
  };

  const handlePersist = async () => {
    if (editedUser) {
      setIsPersisting(true);
      setConfirmModalOpen(false);
      let didError = false;

      // Grant/revoke UV Discovery access
      if (editedUser.updateAccessDiscovery) {
        if (editedUser.permissions.core?.state !== 'deleted') {
          await grantAccess(editedUser, 'discovery').catch((e) => {
            didError = true;
          });
        } else {
          await revokeAccess(editedUser, 'discovery').catch((e) => {
            didError = true;
          });
        }
      }

      // Grant/revoke UV Validation access
      if (editedUser.updateAccessValidation) {
        if (editedUser.permissions.uvk?.state !== 'deleted') {
          await grantAccess(editedUser, 'validation').catch(() => {
            didError = true;
          });
        } else {
          await revokeAccess(editedUser, 'validation').catch(() => {
            didError = true;
          });
        }
      }

      // Short-circuit to list page if no more admin permissions
      if ((!editedUser.permissions.core || editedUser.permissions.core?.state === 'deleted') && (!editedUser.permissions.uvk || editedUser.permissions.uvk?.state === 'deleted')) {
        setIsDirty(false);
        setIsPersisting(false);
        history.push(routes.users.self);
        return;
      }

      // Update permissions
      await updateUserPermissions(
        editedUser.id,
        (editedUser.updateAccessDiscovery || editedUser.permissions.core?.state === 'deleted') ? undefined : editedUser.permissions.core,
        undefined, // Don't support updating uvk permissions for now, doesn't do anything
      ).catch((e) => {
        didError = true;
      });

      // Update teams
      if (Object.keys(teamToggles).length) {
        const addedTeams = [] as number[];
        const removedTeams = [] as number[];
        Object.keys(teamToggles).forEach(key => {
          // @ts-ignore
          (teamToggles[key] === 'on' ? addedTeams : removedTeams).push(key);
        });
        await updateUserTeams(editedUser.id, addedTeams, removedTeams).catch((e) => {
          didError = true;
        });
      }

      setTeamToggles({});
      setIsDirty(false);
      setIsPersisting(false);
      setEditedUser({
        ...editedUser,
        updateAccessDiscovery: false,
        updateAccessValidation: false,
      });
      if (billingRate) {
        updateBillingRate(billingRate);
        setBillingRate(undefined);
      }

      if (didError) {
        enqueueSnackbarError();
      } else {
        enqueueSnackbar('Saved!', { variant: 'success' });
      }
    }
  };

  const updateUser = (user: User) => {
    setIsDirty(true);
    setEditedUser({ ...user });
  };

  const toggleAccess = async (product: ProductType) => {
    if (editedUser) {
      const updatedUser = { ...editedUser };
      if (product === 'validation') {
        updatedUser.updateAccessValidation = !updatedUser.updateAccessValidation;
        if (updatedUser.permissions.uvk) {
          // If we already hav a permission object, toggle its state
          updatedUser.permissions.uvk.state = updatedUser.permissions.uvk.state === 'deleted' ? 'active' : 'deleted';
        } else {
          // Otherwise, must be toggling on, set a default
          updatedUser.permissions.uvk = {
            admin: true,
            state: 'active',
          };
        }
      } else {
        updatedUser.updateAccessDiscovery = !updatedUser.updateAccessDiscovery;
        if (updatedUser.permissions.core) {
          // If we already hav a permission object, toggle its state
          updatedUser.permissions.core.state = updatedUser.permissions.core.state === 'deleted' ? 'active' : 'deleted';
        } else {
          // Otherwise, must be toggling on, set a default
          updatedUser.permissions.core = DEFAULT_CORE_PERMISSIONS;
        }

        if (updatedUser.updateAccessDiscovery) {
          const newBillingRate = calculatePerAdminBillingRateCents(currentAccount!, updatedUser.permissions.core.state === 'active' ? 1 : -1);
          if (newBillingRate) {
            setBillingRate(newBillingRate);
          }
        } else {
          setBillingRate(undefined);
        }
      }

      updateUser(updatedUser);
    }
  };

  const resendInvitation = async (id: number) => {
    setResendingInvitation(true);
    doResendInvitation(id).then(() => {
      enqueueSnackbar('Invitation sent!', { variant: 'success' });
    }).catch((err) => {
      enqueueSnackbarError(err);
    }).finally(() => {
      setResendingInvitation(false);
    });
  };

  return (
    <UserEditContext.Provider
      value={{
        user: editedUser,
        updateUser,
        updateTeam,
        handlePersist,
        isPersisting,
        isDirty,
        loading,
        errors,
        setErrors,
        toggleAccess,
        confirmModalOpen,
        setConfirmModalOpen,
        resendInvitation,
        resendingInvitation,
        billingRate,
      }}
    >
      {children}
      <Prompt when={isDirty} message={(location) => {
        const allowableRoute = generatePath(routes.users.permissions, { userId });
        return location.pathname.startsWith(allowableRoute) ? true : UNSAVED_WARNING_MESSAGE;
      }} />
    </UserEditContext.Provider>
  );
};

